/**
 * DashboardAnalytics Component
 *
 * Analytics tab showing detailed charts, heatmap, and market comparison.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../../hooks/useAnalytics';
import Card from '../../../components/base/Card';
import LineChart from '../../../components/charts/LineChart';
import BarChart from '../../../components/charts/BarChart';
import DonutChart from '../../../components/charts/DonutChart';
import ExportModal from '../../../components/dashboard/ExportModal';
import { GooeyLoader } from '../../../components/ui/gooey-loader';
import { latnLocale } from '../../../utils/numberFormat';

export default function DashboardAnalytics() {
  const { t, i18n } = useTranslation('dashboard');
  const isRTL = i18n.language === 'ar';
  const { overview, timeSeries, propertyAnalytics, marketComparison, loading } = useAnalytics();
  const [showExportModal, setShowExportModal] = useState(false);

  // Transform time series data
  const chartData = timeSeries.map((item) => ({
    name: new Date(item.date).toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'), {
      month: 'short',
      day: 'numeric',
    }),
    views: item.views,
    clicks: item.clicks,
    contacts: item.contacts,
    saves: item.saves,
  }));

  // Transform property data for bar chart
  const topPropertiesData = propertyAnalytics
    .slice(0, 5)
    .map((prop) => ({
      name: prop.propertyTitle.length > 20
        ? prop.propertyTitle.substring(0, 20) + '...'
        : prop.propertyTitle,
      value: prop.views,
      color: '#004D40',
    }));

  // Donut chart data for metrics distribution
  const metricsDistribution = [
    { name: t('stats.views'), value: overview?.totalViews || 0, color: '#004D40' },
    { name: t('stats.clicks'), value: overview?.totalClicks || 0, color: '#B58A0A' },
    { name: t('stats.contacts'), value: overview?.totalContacts || 0, color: '#10b981' },
    { name: t('stats.saves'), value: overview?.totalSaves || 0, color: '#f59e0b' },
  ];

  // Heatmap data (simplified view - hours of day)
  const heatmapHours = Array.from({ length: 24 }, (_, i) => ({
    name: `${i.toString().padStart(2, '0')}:00`,
    value: Math.floor(Math.random() * 100), // Mock data - replace with actual
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors"
        >
          <i className="ri-download-line"></i>
          {t('export.title')}
        </button>
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Over Time */}
        <Card variant="bordered" padding="lg">
          <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
            {t('analytics.performanceOverTime')}
          </h3>
          {chartData.length > 0 ? (
            <LineChart
              data={chartData}
              lines={[
                { dataKey: 'views', name: t('stats.views'), color: '#004D40' },
                { dataKey: 'clicks', name: t('stats.clicks'), color: '#B58A0A' },
                { dataKey: 'contacts', name: t('stats.contacts'), color: '#10b981' },
              ]}
              height={300}
              showLegend
            />
          ) : (
            <EmptyState message={t('emptyStates.noData')} />
          )}
        </Card>

        {/* Metrics Distribution */}
        <Card variant="bordered" padding="lg">
          <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
            {t('analytics.metricsDistribution') || 'Metrics Distribution'}
          </h3>
          <DonutChart
            data={metricsDistribution}
            height={300}
            centerValue={overview?.totalViews || 0}
            centerLabel={t('stats.views')}
            showLegend
          />
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Properties */}
        <Card variant="bordered" padding="lg">
          <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
            {t('analytics.topProperties')}
          </h3>
          {topPropertiesData.length > 0 ? (
            <BarChart
              data={topPropertiesData}
              height={300}
              layout="vertical"
              colorByData
            />
          ) : (
            <EmptyState message={t('emptyStates.noData')} />
          )}
        </Card>

        {/* Activity Heatmap */}
        <Card variant="bordered" padding="lg">
          <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
            {t('analytics.heatmap')}
          </h3>
          <p className="text-sm text-[var(--pv-color-text-muted)] mb-4">
            {t('analytics.heatmapDescription')}
          </p>
          <div className="grid grid-cols-6 gap-1">
            {heatmapHours.map((hour, index) => {
              const intensity = hour.value / 100;
              return (
                <div
                  key={index}
                  className="aspect-square rounded flex items-center justify-center text-xs"
                  style={{
                    backgroundColor: `rgba(0,77,64, ${intensity * 0.8 + 0.1})`,
                    color: intensity > 0.5 ? 'white' : 'var(--pv-color-text-muted)',
                  }}
                  title={`${hour.name}: ${hour.value} ${t('stats.views')}`}
                >
                  {index % 4 === 0 ? hour.name.split(':')[0] : ''}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Market Comparison */}
      <Card variant="bordered" padding="lg">
        <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
          {t('analytics.marketComparison')}
        </h3>
        {marketComparison.length > 0 ? (
          <div className="space-y-4">
            {marketComparison.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[var(--pv-color-text)]">
                    {item.metric}
                  </span>
                  <span className={`text-sm font-medium ${
                    item.yourValue >= item.marketAverage
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}>
                    {item.yourValue >= item.marketAverage
                      ? t('analytics.aboveAverage')
                      : t('analytics.belowAverage')}
                    {' '}({item.percentile}%)
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-[var(--pv-color-text-muted)] mb-1">
                      <span>{t('analytics.yourPerformance')}: {item.yourValue}</span>
                      <span>{t('analytics.marketAverage')}: {item.marketAverage}</span>
                    </div>
                    <div className="relative h-2 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded-full overflow-hidden">
                      <div
                        className="absolute h-full bg-[var(--pv-color-primary)] rounded-full"
                        style={{ width: `${Math.min((item.yourValue / (item.marketAverage * 2)) * 100, 100)}%` }}
                      />
                      <div
                        className="absolute h-full w-0.5 bg-amber-500"
                        style={{ left: '50%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState message={t('emptyStates.noData')} />
        )}
      </Card>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-64 text-[var(--pv-color-text-muted)]">
      <div className="text-center">
        <i className="ri-bar-chart-line text-4xl mb-2 block opacity-50"></i>
        <p>{message}</p>
      </div>
    </div>
  );
}
